import { notificationService } from '@/services/notificationService'
import type { RegistrationData, RegistrationErrors } from '@/types'
import { ErrorHandler } from '@/utils/ErrorHandler'
import { sanitizeEmail, sanitizePassword, validateEmail, validatePassword, validateRegistration } from '@/utils/validation'
import { type ChangeEvent, useEffect, useState } from 'react'
import { useRegistrationFlow } from '../../hooks/useRegistrationFlow'
import useBrandUserStore from '../../store/brandUserStore'
import { logError } from '../../utils/sentry'

import RegistrationForm from './RegistrationForm'
import styles from './registration.module.css'

interface RegistrationProps {
  onLinkClick: (page: string) => void
  onComplete: (email: string) => void
}

interface ValidationErrorWithFields {
  fieldErrors?: Record<string, string>
  message?: string
}

export const Registration = ({ onLinkClick, onComplete }: RegistrationProps) => {
  const { setValue, error: storeErrors, clearErrors } = useBrandUserStore()
  const { nextStep } = useRegistrationFlow()

  const [values, setValues] = useState<RegistrationData>({
    email: '',
    password: '',
    confirmPassword: '',
  })

  const [, setErrors] = useState<RegistrationErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [generalError, setGeneralError] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [serverErrorLock, setServerErrorLock] = useState(false)

  useEffect(() => {
    if (storeErrors && Array.isArray(storeErrors) && storeErrors.length > 0) {
      try {
        const errorsRecord: Record<string, string[]> = {}
        for (const errEntry of storeErrors) {
          if (typeof errEntry === 'string') {
            setGeneralError(errEntry)
          } else if (typeof errEntry === 'object' && errEntry !== null) {
            const errObj = errEntry as Record<string, unknown>
            const field = 'field' in errObj ? String(errObj.field) : 'general'
            const message = 'message' in errObj ? String(errObj.message) : String(errObj)

            if (!errorsRecord[field]) {
              errorsRecord[field] = []
            }
            errorsRecord[field].push(message)
          }
        }

        if (Object.keys(errorsRecord).length > 0) {
          const normalizedErrors = ErrorHandler.handleValidationErrors(errorsRecord)

          if (normalizedErrors.general) {
            setGeneralError(normalizedErrors.general)
          } else {
            const firstError = Object.values(normalizedErrors)[0]
            setGeneralError(firstError || 'Error in form validation')
          }
        }
      } catch (e) {
        console.error('Error processing validation errors:', e)
        setGeneralError('Error processing validation errors')
      }
    }
  }, [storeErrors])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value: rawValue } = e.target

    let value = rawValue
    if (id === 'email') {
      value = sanitizeEmail(rawValue)
    } else if (id === 'password' || id === 'confirmPassword') {
      value = sanitizePassword(rawValue)
    }

    const nextValues = { ...values, [id]: value }
    setValues(nextValues)
    setErrors((prev) => {
      const updated: RegistrationErrors = { ...prev }

      if (id === 'email') {
        const emailErr = validateEmail(nextValues.email || '')
        if (emailErr) updated.email = emailErr
        else updated.email = undefined
      }

      if (id === 'password') {
        const pwdErr = validatePassword(nextValues.password || '')
        if (pwdErr) updated.password = pwdErr
        else updated.password = undefined

        if (nextValues.confirmPassword) {
          if (nextValues.password !== nextValues.confirmPassword) {
            updated.confirmPassword = 'Passwords must match'
          } else {
            updated.confirmPassword = undefined
          }
        }
      }

      if (id === 'confirmPassword') {
        if ((nextValues.password || '') !== (nextValues.confirmPassword || '')) {
          updated.confirmPassword = 'Passwords must match'
        } else {
          updated.confirmPassword = undefined
        }
      }

      return updated
    })

    setValue(id, value)

    if (generalError) {
      setGeneralError('')
    }

    if (serverErrorLock) {
      setServerErrorLock(false)
    }

    if (storeErrors && storeErrors.length > 0) {
      clearErrors()
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    try {
      const result = await nextStep(values)

      if (result) {
        if (onComplete) {
          onComplete(values.email)
        }
      } else {
        logError('Failed to complete registration step')
        notificationService.error('Failed to complete registration')
        setServerErrorLock(true)
        setIsSubmitting(false)
      }
    } catch (error: unknown) {
      const fieldErrors = (error as ValidationErrorWithFields).fieldErrors

      if (fieldErrors) {
        setErrors(fieldErrors)

        if (fieldErrors.email) {
          notificationService.error(fieldErrors.email)
          setIsSubmitting(false)
          return
        }
      }

      const axiosError = error as {
        response?: {
          data?: {
            data?: Record<string, string>
            message?: string
            [key: string]: unknown
          }
          status?: number
        }
      }

      if (axiosError.response?.data) {
        const responseData = axiosError.response.data

        const fullResponseString = JSON.stringify(responseData)

        if (fullResponseString.includes('already exists')) {
          let emailError = ''

          if (responseData.data?.email) {
            emailError = responseData.data.email
          } else if (fullResponseString.includes('User with email')) {
            const match = fullResponseString.match(/User with email ['"]([^'"]+)['"] already exists/)
            if (match) {
              emailError = `User with email ${match[1]} already exists`
            } else {
              emailError = 'Email already exists'
            }
          }

          if (emailError) {
            setErrors({ email: emailError })
            notificationService.error(emailError)
            setServerErrorLock(true)
            return
          }
        }

        if (responseData.message === 'Validation failed' && responseData.data) {
          const validationErrors: Record<string, string> = {}

          for (const [field, message] of Object.entries(responseData.data)) {
            validationErrors[field] = message as string
          }

          if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            const firstError = Object.values(validationErrors)[0]
            notificationService.error(firstError)
            setServerErrorLock(true)
            setIsSubmitting(false)
            return
          }
        }

        const errorMessage = responseData.message || 'Registration failed'
        setGeneralError(errorMessage)
        notificationService.error(errorMessage)
        setServerErrorLock(true)
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Registration failed'
        setGeneralError(errorMessage)
        notificationService.error(errorMessage)
        setServerErrorLock(true)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const validateForm = () => {
    const validationErrors = validateRegistration(values)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      // Show first validation error in toast
      const firstError = Object.values(validationErrors)[0]
      if (firstError) {
        notificationService.error(firstError)
      }
      return false
    }
    return true
  }

  return (
    <div className={styles.mainContentWrapper}>
      <h1 className={styles.title}>Sign Up</h1>
      <p className={styles.textGrey}>Welcome to our world!</p>

      <RegistrationForm
        values={values}
        // errors={error}
        // generalError={generalError}
        isSubmitting={isSubmitting}
        acceptedTerms={acceptedTerms}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onBack={() => {
          onLinkClick('login')
        }}
        onSignIn={() => onLinkClick('login')}
        onToggleAcceptTerms={(checked) => {
          setAcceptedTerms(checked)
          if (serverErrorLock) setServerErrorLock(false)
        }}
      />
    </div>
  )
}
